import { useRef, useState } from 'react'
import { BRANDS, FUEL_TYPES, GALLON_OPTIONS, getSymptom } from '../config/constants'
import { useEstimate } from '../context/EstimateContext'
import { Camera, Pencil } from '../components/icons'
import ErrorNotice from '../components/ErrorNotice'
import Select from '../components/Select'

export default function PhotoStep() {
  const { symptom, entryMode, setEntryMode, submitPhoto, submitManual, error } = useEstimate()
  const sym = getSymptom(symptom)

  return (
    <div className="animate-fade-in">
      {sym && (
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-brand-100 bg-white/70 px-3 py-1.5 text-xs font-semibold text-brand-700 backdrop-blur">
          <span aria-hidden="true">{sym.emoji}</span>
          {sym.label}
        </div>
      )}

      <ErrorNotice message={error} />

      {entryMode === 'photo' ? (
        <PhotoCapture onCapture={submitPhoto} onManual={() => setEntryMode('manual')} />
      ) : (
        <ManualEntry onSubmit={submitManual} onBackToPhoto={() => setEntryMode('photo')} />
      )}
    </div>
  )
}

function PhotoCapture({ onCapture, onManual }) {
  const inputRef = useRef(null)

  function handleFile(e) {
    const file = e.target.files?.[0]
    if (file) onCapture(file)
  }

  return (
    <div>
      <h1 className="text-[26px] font-bold leading-[1.15] text-ink">Take a photo of the sticker</h1>
      <p className="mt-2.5 text-sm leading-relaxed text-muted">
        It&apos;s the metal or foil <span className="font-medium text-subink">data plate</span> on the
        side of the tank. We read the brand, size, and age from it automatically.
      </p>

      <ExamplePlate />

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFile}
      />

      <button className="btn-primary mt-6 flex items-center justify-center gap-2" onClick={() => inputRef.current?.click()}>
        <Camera className="h-5 w-5" />
        Take / upload photo
      </button>

      <button className="btn-secondary mt-3 flex items-center justify-center gap-2" onClick={onManual}>
        <Pencil className="h-4 w-4" />
        Enter the details by hand instead
      </button>

      <p className="mt-4 text-center text-[11px] text-muted">
        Your photo is used only to prepare your estimate.
      </p>
    </div>
  )
}

// Stylized data plate so people know what to look for — no external asset.
function ExamplePlate() {
  return (
    <div className="mt-6 rounded-3xl border border-dashed border-brand-200 bg-white/60 p-4 backdrop-blur">
      <p className="mb-2.5 eyebrow text-brand-600">Example — look for this</p>
      <div className="rounded-2xl bg-slate-900 p-4 font-mono text-[11px] leading-relaxed text-slate-100 shadow-inner">
        <div className="flex justify-between">
          <span className="text-slate-400">BRAND</span>
          <span>RHEEM</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-400">MODEL</span>
          <span>XE40M06ST45U1</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-400">SERIAL</span>
          <span className="text-aqua-300">Q091531234</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-400">CAPACITY</span>
          <span>40 GAL</span>
        </div>
      </div>
      <p className="mt-2.5 text-[11px] leading-snug text-muted">
        Usually near the top or on the front. Wipe off dust and use good light — the serial number
        tells us how old it is.
      </p>
    </div>
  )
}

function ManualEntry({ onSubmit, onBackToPhoto }) {
  const [brand, setBrand] = useState('')
  const [gallons, setGallons] = useState('')
  const [fuelType, setFuelType] = useState('')
  const [approxAge, setApproxAge] = useState('')

  function handleSubmit(e) {
    e.preventDefault()
    onSubmit({
      brand: brand || null,
      gallons: gallons ? Number(gallons) : null,
      fuelType: fuelType || 'unknown',
      approxAge: approxAge !== '' ? Number(approxAge) : null,
    })
  }

  return (
    <form onSubmit={handleSubmit}>
      <h1 className="text-[26px] font-bold leading-[1.15] text-ink">Tell us about your unit</h1>
      <p className="mt-2.5 text-sm leading-relaxed text-muted">
        No sticker handy? Best guesses are fine — your tech confirms everything on-site.
      </p>

      <div className="mt-6 space-y-4">
        <Field label="Brand">
          <Select
            ariaLabel="Brand"
            value={brand}
            onChange={setBrand}
            placeholder="Select a brand…"
            options={BRANDS.map((b) => ({ value: b, label: b }))}
          />
        </Field>

        <Field label="Tank size">
          <Select
            ariaLabel="Tank size"
            value={gallons}
            onChange={setGallons}
            placeholder="Not sure"
            options={[
              { value: '', label: 'Not sure' },
              ...GALLON_OPTIONS.map((g) => ({ value: String(g), label: `${g} gallon` })),
            ]}
          />
        </Field>

        <Field label="Power source">
          <div className="grid grid-cols-1 gap-2">
            {FUEL_TYPES.map((f) => (
              <label
                key={f.key}
                className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-3.5 text-sm transition ${
                  fuelType === f.key
                    ? 'border-brand-400 bg-brand-50 font-semibold text-brand-800 ring-2 ring-brand-100'
                    : 'border-hairline bg-white/70 text-subink backdrop-blur'
                }`}
              >
                <input
                  type="radio"
                  name="fuelType"
                  value={f.key}
                  checked={fuelType === f.key}
                  onChange={(e) => setFuelType(e.target.value)}
                  className="accent-brand-600"
                />
                {f.label}
              </label>
            ))}
          </div>
        </Field>

        <Field label="Roughly how old is it? (years)" hint="A guess is completely fine.">
          <input
            className="input"
            type="number"
            inputMode="numeric"
            min="0"
            max="60"
            placeholder="e.g. 10"
            value={approxAge}
            onChange={(e) => setApproxAge(e.target.value)}
          />
        </Field>
      </div>

      <button type="submit" className="btn-primary mt-7">
        See my estimate
      </button>
      <button type="button" className="btn-secondary mt-3 flex items-center justify-center gap-2" onClick={onBackToPhoto}>
        <Camera className="h-4 w-4" />
        Use the photo scanner instead
      </button>
    </form>
  )
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-subink">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-muted">{hint}</span>}
    </label>
  )
}
